// Sentry crash + error reporting.
//
// Why a module rather than inline in index.js: every catch block that wants to
// report needs a single import that is SAFE TO CALL IN DEV and in demo mode,
// where Sentry is never initialised. reportError() below is that call — it is a
// no-op unless init actually ran, so call sites never need to guard.
//
// PRIVACY CONTRACT (this is the part to re-read before changing anything here):
//   - We send the Supabase user id and NOTHING else that identifies a person.
//     No email, no username, no IP.
//   - Request/response BODIES are never sent. Coach messages, journal text and
//     Plaid account data all travel in bodies, so the rule is absolute rather
//     than a denylist of endpoints: we strip the body no matter the URL.
//   - Breadcrumbs are the sneaky leak path, not events. See beforeBreadcrumb.
import * as Sentry from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import pkg from '../package.json';
import { scrubEvent, scrubBreadcrumb, stripQuery } from './sentryScrub';

const DSN = process.env.REACT_APP_SENTRY_DSN;

// A PRODUCTION build served from a laptop is still a production build
// (NODE_ENV baked at `npm run build`), so `serve build` on localhost:4173 was
// reporting as environment "production" and tripping the alert emails
// (SCORIFY-APP-8/9 came from these test runs). Distinguish by RUNTIME host,
// NOT by the build flag:
//   - Native (Capacitor) is always the real app, EVEN THOUGH its WebView host
//     is capacitor://localhost — so isNativePlatform() must be checked before
//     any hostname test, or the shipped iOS app would be misread as "local".
//   - Web on localhost / 127.0.0.1 / a LAN IP / *.local is a local or preview
//     run; anything else (scorify.to) is real web.
// Local runs do not initialise Sentry at all: zero events, no alert noise.
const isLocalWebHost = () => {
  try {
    if (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform && Capacitor.isNativePlatform()) return false;
    const h = (typeof window !== 'undefined' && window.location && window.location.hostname) || '';
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.local')
      || /^192\.168\./.test(h) || /^10\./.test(h) || /^172\.(1[6-9]|2\d|3[01])\./.test(h);
  } catch { return false; }
};

// Production builds only, and never a local/preview host. `npm start` is
// development; a missing DSN also disables rather than throwing.
export const sentryEnabled = process.env.NODE_ENV === 'production' && !!DSN && !isLocalWebHost();

// RELEASE TAGGING: every event must say which build it came from, or a
// TestFlight bug report is untraceable to a binary.
//   iOS   -> release "to.scorify.app@<version>+<build>" and dist "<build>",
//            read at runtime from the ACTUAL bundle via App.getInfo(), so it
//            can never drift from what Xcode shipped (e.g. 1.0.1 (6) becomes
//            to.scorify.app@1.0.1+6, dist 6).
//   web   -> release "scorify-web@<package.json version>". Bump package.json
//            when MARKETING_VERSION bumps; the native path does not depend on
//            it either way.
// getInfo() is one fast native bridge call; the few-ms init delay on iOS is
// the price of a correct release and is accepted. Web initialises
// synchronously exactly as before.
export function initSentry() {
  if (!sentryEnabled) return;
  const webRelease = `scorify-web@${pkg.version}`;
  if (Capacitor.isNativePlatform()) {
    CapacitorApp.getInfo()
      .then((info) => doInit(`to.scorify.app@${info.version}+${info.build}`, String(info.build)))
      .catch(() => doInit(webRelease));
  } else {
    doInit(webRelease);
  }
}

function doInit(release, dist) {
  Sentry.init(
    {
      dsn: DSN,
      // The DSN itself encodes the Sentry region (ingest.de.sentry.io for our
      // EU org), so there is no separate region option to set here.
      environment: 'production',
      release,
      ...(dist ? { dist } : {}),

      // Error reporting, not performance monitoring.
      tracesSampleRate: 0.1,

      // LAUNCH-SPEED: cut breadcrumb volume. Xcode flagged main-thread I/O
      // inside Sentry (SentryAutoBreadcrumb → addBreadcrumb →
      // SentryWatchdogTermination → file I/O): the native watchdog persists the
      // scope to disk as breadcrumbs accumulate, and this app is a heavy logger,
      // so every console line became a breadcrumb synced to native and written.
      // Two fixes below:
      //   1. maxBreadcrumbs 100 -> 30: a smaller buffer = smaller/faster persist.
      //   2. integrations: turn OFF console + dom breadcrumb CREATION at the
      //      source (not just drop them in beforeBreadcrumb, which still pays the
      //      per-log cost). Network (fetch/xhr) + navigation breadcrumbs stay —
      //      failed requests are this app's main diagnostic. Watchdog
      //      termination tracking is KEPT (it caught SCORIFY-APP-7); we only
      //      shrink what it has to write, we do not disable it.
      maxBreadcrumbs: 30,
      enableWatchdogTerminationTracking: true,
      integrations: (defaultIntegrations) =>
        defaultIntegrations.map((integration) =>
          integration.name === 'Breadcrumbs'
            ? SentryReact.breadcrumbsIntegration({ console: false, dom: false })
            : integration
        ),

      // Governs IP address, cookies and headers. Must stay false.
      sendDefaultPii: false,

      // Session Replay would record the DOM — journal text, coach replies and
      // balances are all on screen. Never enable it for this app.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,

      beforeSend: (event) => scrubEvent(event),

      beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
    },
    // Wires the React-specific SDK underneath the Capacitor one. On iOS this
    // also starts the native layer so hard crashes (not just JS errors) report.
    SentryReact.init
  );
}

// Attach / clear the Supabase user id. Called from the auth state listener.
export function setSentryUser(userId) {
  if (!sentryEnabled) return;
  Sentry.setUser(userId ? { id: String(userId) } : null);
}

// Report a HANDLED error. Safe to call anywhere, including dev and demo mode.
//
// `context` must contain only non-identifying, non-user-generated values —
// endpoint paths, HTTP status codes, operation names. Never pass a request
// body, a journal entry, a coach message or a Plaid payload.
// Transport-shaped failure messages, as produced by fetch/postgrest-js when a
// request never reaches a server ("TypeError: Load failed" in WKWebView,
// "Failed to fetch" in Chromium). Used only to drop offline weather below.
const TRANSPORT_FAILURE_RE = /load failed|failed to fetch|networkerror|network connection was lost|fetcherror|the internet connection appears to be offline/i;

export function reportError(error, context = {}) {
  if (!sentryEnabled) return;
  // Offline weather, not bugs: when the device KNOWS it is offline
  // (navigator.onLine === false — the trustworthy direction of that flag) a
  // transport-level failure is expected and reporting it would bury real
  // errors under a burst of "Load failed" events every time a user rides an
  // elevator. Non-transport errors still report even offline — the native
  // sentry-cocoa transport disk-caches and delivers them on reconnect.
  try {
    if (typeof navigator !== 'undefined' && navigator.onLine === false &&
        TRANSPORT_FAILURE_RE.test(String(error?.message || error || ''))) return;
  } catch { /* detection must never block reporting */ }
  try {
    Sentry.withScope((scope) => {
      scope.setLevel(context.level || 'error');
      const { level, fingerprint, ...rest } = context;
      // Stable grouping for a known condition, so repeats land on one issue
      // instead of splitting by message text (e.g. healthkit-auth-sheet-failed).
      if (Array.isArray(fingerprint) && fingerprint.length) scope.setFingerprint(fingerprint);
      // OS version as a searchable tag. JS cannot read it (WKWebView's UA
      // OS version is frozen), but the native layer attaches contexts.os by
      // the time the event is processed — copy it from there.
      scope.addEventProcessor((event) => {
        const v = event?.contexts?.os?.version;
        if (v) event.tags = { ...(event.tags || {}), os_version: String(v) };
        return event;
      });
      Object.entries(rest).forEach(([k, v]) => {
        if (v !== undefined && v !== null) scope.setTag(k, String(v).slice(0, 200));
      });
      Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
    });
  } catch {
    // Reporting must never break the code path it is observing.
  }
}

// Report a non-exception CONDITION (e.g. a UI health check that found the
// screen degraded). Same contract as reportError: tags must be non-PII
// primitives; never throws; no-op unless Sentry initialised.
export function reportMessage(message, context = {}) {
  if (!sentryEnabled) return;
  try {
    Sentry.withScope((scope) => {
      scope.setLevel(context.level || 'warning');
      const { level, fingerprint, ...rest } = context;
      if (Array.isArray(fingerprint) && fingerprint.length) scope.setFingerprint(fingerprint);
      Object.entries(rest).forEach(([k, v]) => {
        if (v !== undefined && v !== null) scope.setTag(k, String(v).slice(0, 200));
      });
      Sentry.captureMessage(String(message));
    });
  } catch {
    // Reporting must never break the code path it is observing.
  }
}

// Non-error breadcrumb for the trail that precedes a future error (e.g. the
// LOCKSEQ unlock beats + appStateChange, so a SCORIFY-APP-F Face-ID-hang event
// shows whether the app backgrounded while Face ID was pending). Data must be
// NON-PII primitives only — breadcrumbs ship attached to whatever error fires
// next; never a user id, name, or any health/financial value. Never throws.
export function addBreadcrumb(category, message, data) {
  if (!sentryEnabled) return;
  try {
    Sentry.addBreadcrumb({
      category, message, level: 'info',
      ...(data && typeof data === 'object' ? { data } : {}),
    });
  } catch { /* observing must never break the observed path */ }
}

export { stripQuery };
export default Sentry;
